import { House } from '../../data/houses';

interface HouseSelectorProps {
  houses: House[];
  selectedHouse: House;
  onSelectHouse: (house: House) => void;
}

export default function HouseSelector({
  houses,
  selectedHouse,
  onSelectHouse,
}: HouseSelectorProps) {


  // For 5 houses, we need custom positioning for the bottom row
  const renderHouses = () => {
    // If we have 5 houses, we want to center the last 2
    // But actually, the user wants them balanced.
    // The previous implementation had 3 on top and 2 on bottom but hardcoded widths.
    // Let's use flex wrap and justify-center to handle any number of odd items gracefully, 
    // or keep the specific 3/2 split but make it more responsive.

    // Let's try a centered flex layout that naturally wraps.
    // This often looks better than a grid with holes.
    return (
      <div className="flex flex-wrap justify-center gap-6 w-full max-w-6xl">
        {houses.map(house => (
          <div key={house.id} className="w-full sm:w-[calc(50%-12px)] md:w-[calc(33.333%-16px)] lg:w-[calc(20%-16px)] flex-grow" style={{ minWidth: '200px', maxWidth: '300px' }}>
            <HouseButton
              house={house}
              isSelected={house.id === selectedHouse.id}
              onSelect={onSelectHouse}
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="w-full flex justify-center px-4">
      {renderHouses()}
    </div>
  );
}

interface HouseButtonProps {
  house: House;
  isSelected: boolean;
  onSelect: (house: House) => void;
}

function HouseButton({ house, isSelected, onSelect }: HouseButtonProps) {
  return (
    <button
      onClick={() => onSelect(house)}
      className={`w-full relative backdrop-blur-lg bg-white/10 rounded-2xl p-8 transition-all duration-300 border-2 ${isSelected
        ? 'scale-105 shadow-2xl'
        : 'scale-100 opacity-70 hover:opacity-90 hover:scale-102'
        }`}
      style={{
        borderColor: isSelected ? house.color : 'rgba(255,255,255,0.2)',
        boxShadow: isSelected
          ? `0 8px 32px ${house.color}60, 0 0 40px ${house.color}40`
          : '0 4px 16px rgba(0,0,0,0.2)',
      }}
    >
      <div className="flex flex-col items-center space-y-4">
        <span className="text-6xl md:text-7xl">{house.icon}</span>
        <div className="text-center">
          <h3
            className="font-bold text-xl md:text-2xl tracking-wide"
            style={{ color: isSelected ? house.accent : '#fff' }}
          >
            {house.name.toUpperCase()}
          </h3>
        </div>
        <div
          className={`w-4 h-4 rounded-full transition-all duration-300 ${isSelected ? 'scale-100' : 'scale-0'
            }`}
          style={{ backgroundColor: house.accent }}
        />
      </div>

      {isSelected && (
        <div
          className="absolute inset-0 rounded-2xl opacity-20 animate-pulse"
          style={{ backgroundColor: house.color }}
        />
      )}
    </button>
  );
}
